import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { TypedDocumentNode } from '@apollo/client';
import { toast } from 'react-toastify';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { IBlackjackRoom } from '@api/types/blackjack';
import { getActivePlayersInRoom } from './blackjack';
import { ICoinflipGame } from '@api/types/Coinflip';

export function calcWinRatio({
    games_won,
    games_lose,
}: {
    games_won: number;
    games_lose: number;
}): number {
    return (
        Number(((games_won / (games_won + games_lose)) * 100).toFixed(0)) || 0
    );
}

export function toastSuccessMessage(msg: string): void {
    toast.success(msg, {
        autoClose: 3000,
        pauseOnHover: true,
        closeOnClick: true,
    });
}

export function toastErrorMessage(msg: string): void {
    toast.error(msg, {
        autoClose: 3000,
        pauseOnHover: true,
        closeOnClick: true,
    });
}

export const getUserIdFromToken = (): string => {
    const token = localStorage.getItem('token');

    return token ? (jwtDecode(token) as any).id : '';
};

interface IGetPlayerParams<T> {
    players: T[];
    id: string;
    key?: 'id' | '_id';
}

// export const getPlayerById = <T>(players: T[], id: string): T | undefined =>
//   players.find((player: any) => player.user.id === id);

export const getPlayerById = <T>({
    players,
    id,
    key = '_id',
}: IGetPlayerParams<T>): T | undefined =>
    players.find((player: any) => player.user[key] === id);

export const getPlayerIndexById = <T>({
    players,
    id,
    key = '_id',
}: IGetPlayerParams<T>): number =>
    players.findIndex((player: any) => player.user[key] === id);

export async function graphqlQuery({
    query,
    variables,
}: {
    query: TypedDocumentNode<any, object>;
    variables: any;
}): Promise<any> {
    const token = localStorage.getItem('token');

    return (
        await axios.post(
            process.env.REACT_APP_BACKEND_SERVER,
            {
                query: query.loc?.source.body,
                variables,
            },
            {
                headers: {
                    authorization: token ? `Bearer ${token}` : '',
                },
            },
        )
    ).data.data;
}

export function convertSOLammount(ammount: number): number {
    return ammount / Math.pow(10, 9);
}

export function convertDuelanaPointsToSOL(amount: number) {
    const pointsAmount = ((amount * 5) / 1000) * LAMPORTS_PER_SOL;
    return pointsAmount >= 1 ? Math.floor(pointsAmount) : pointsAmount;
}

export function convertSOLtoDuelanaPoints(ammount: number): number {
    const pointsAmount = (convertSOLammount(ammount) * 1000) / 5;
    return pointsAmount >= 1 ? Math.floor(pointsAmount) : pointsAmount;
}

export async function ConnectWallet() {
    if ('solana' in window) {
        const provider = (window as any).solana;
        if (provider.isPhantom) {
            return new PhantomWalletAdapter(provider);
        }
    }
    window.open('https://phantom.app/', '_blank');
}

export const formatThousandNumber = (x: number): string =>
    new Intl.NumberFormat('en-US').format(x);

export function numberWithSeparator(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const getActivePlayersInBlackjack = (rooms: IBlackjackRoom[]): number =>
    rooms.reduce(
        (playersCount: number, room: IBlackjackRoom) =>
            getActivePlayersInRoom(room) + playersCount,
        0,
    );

export const getActivePlayersInCoinflip = (games: ICoinflipGame[]) =>
    games.reduce((playersCount: number, game: ICoinflipGame) => {
        if (game.game_metadata.status === 'finished') return playersCount;

        game.heads_user && playersCount++;
        game.tails_user && playersCount++;

        return playersCount;
    }, 0);

export const copyToClipboard = (str: string) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText)
        return navigator.clipboard.writeText(str);
    return Promise.reject('The Clipboard API is not available.');
};

export function isNumeric(n: string): boolean {
    return !isNaN(parseInt(n));
}
